import { useState } from "react";
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { auth } from "../../configs/firebase";
import {
  completeWhatsAppProfile,
  isDuplicatePhoneError,
} from "./whatsappAuthHelpers";

const WhatsAppCompleteProfilePage = () => {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSkipping, setIsSkipping] = useState(false);

  const toast = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("Please sign in again.");
      }

      setIsLoading(true);

      await completeWhatsAppProfile({
        firebaseUid: currentUser.uid,
        username,
        email: email || undefined,
        password: password || undefined,
      });

      toast({
        title: "Profile completed",
        description: "Your account is now fully set up.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });

      navigate("/dashboard");
    } catch (error: any) {
      const message = error?.message || "Profile setup failed.";

      toast({
        title: isDuplicatePhoneError(message)
          ? "Phone number already linked"
          : "Profile setup failed",
        description: message,
        status: "error",
        duration: 7000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompleteLater = () => {
    setIsSkipping(true);

    toast({
      title: "You can complete your profile later",
      description:
        "Your WhatsApp sign-in is active. You can finish setting your username, email, and password from your profile settings.",
      status: "info",
      duration: 4500,
      isClosable: true,
    });

    navigate("/dashboard");
  };

  return (
    <Container maxW="md" py={10}>
      <Box p={8} borderWidth="1px" borderRadius="xl" boxShadow="lg">
        <Heading size="lg" mb={2}>
          Complete your account
        </Heading>

        <Text mb={3} color="gray.500">
          Set your username and password so your account is fully secured.
        </Text>

        <Text mb={6} color="gray.500" fontSize="sm">
          You can also complete this later from your profile settings.
        </Text>

        <form onSubmit={handleSubmit}>
          <Stack spacing={4}>
            <FormControl isRequired>
              <FormLabel>Username</FormLabel>
              <Input
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                placeholder="Choose a username"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Email</FormLabel>
              <Input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@example.com"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Password</FormLabel>
              <Input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Set a secure password"
              />
            </FormControl>

            <Button
              type="submit"
              bg="teal.400"
              color="white"
              _hover={{ bg: "teal.500" }}
              isLoading={isLoading}
              isDisabled={isSkipping}
            >
              Save and Continue
            </Button>

            <Button
              type="button"
              variant="outline"
              borderColor="teal.400"
              color="teal.500"
              _hover={{ bg: "teal.50" }}
              onClick={handleCompleteLater}
              isLoading={isSkipping}
              isDisabled={isLoading}
            >
              Complete Profile Later
            </Button>

            <Text textAlign="center" color="gray.500" fontSize="xs">
              You will still be signed in, but some account features may require
              a completed profile later.
            </Text>
          </Stack>
        </form>
      </Box>
    </Container>
  );
};

export default WhatsAppCompleteProfilePage;